import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, CheckCircle, Volume2 } from "lucide-react";

// Waveform animation component
const WaveformIcon = ({ isPlaying, isHovered }) => {
  return (
    <div className="flex items-center gap-[2px] h-5">
      {[0, 1, 2, 3, 4].map((i) => (
        <motion.div
          key={i}
          className="w-[3px] bg-white rounded-full"
          animate={{
            height: isPlaying 
              ? [8, 16, 10, 20, 12][i % 5] 
              : isHovered 
                ? [6, 10, 8, 12, 6][i % 5]
                : 8,
            scaleY: isPlaying ? [1, 1.5, 1, 1.8, 1.2] : 1
          }}
          transition={{
            duration: isPlaying ? 0.4 : 0.3,
            repeat: isPlaying ? Infinity : 0,
            repeatType: "reverse",
            delay: i * 0.1,
            ease: "easeInOut"
          }}
          style={{ minHeight: 4 }}
        />
      ))}
    </div>
  );
};

// Progress bar component
const ProgressBar = ({ progress, duration, currentTime, onSeek }) => {
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="w-full space-y-2">
      <div 
        className="relative h-1.5 bg-white/10 rounded-full cursor-pointer group"
        onClick={onSeek}
      >
        <motion.div 
          className="absolute left-0 top-0 h-full bg-gradient-to-r from-[#FF6600] to-[#FF4500] rounded-full"
          style={{ width: `${progress}%` }}
        />
        <motion.div
          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ left: `calc(${progress}% - 6px)` }}
        />
      </div>
      <div className="flex justify-between text-xs text-[#A0A0A0] font-mono">
        <span>{formatTime(currentTime)}</span>
        <span>{formatTime(duration)}</span>
      </div>
    </div>
  );
};

const AudioDemoPlayer = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(103); // Default 1:43, will be updated when audio loads
  const [hasCompleted, setHasCompleted] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const audioRef = useRef(null);

  // Handle audio metadata loaded
  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      setIsLoaded(true);
    }
  };

  // Handle audio time update
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const current = audioRef.current.currentTime;
      const total = audioRef.current.duration;
      setCurrentTime(current);
      setProgress((current / total) * 100);
    }
  };

  // Handle audio ended
  const handleEnded = () => {
    setIsPlaying(false);
    setHasCompleted(true);
  };

  // Handle play/pause
  const togglePlay = () => {
    if (!isExpanded) {
      setIsExpanded(true);
      // Small delay before playing
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.play();
          setIsPlaying(true);
        }
      }, 300);
    } else {
      if (audioRef.current) {
        if (isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
        } else {
          audioRef.current.play();
          setIsPlaying(true);
        }
      }
    }
  };

  // Reset demo
  const resetDemo = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
    setProgress(0);
    setCurrentTime(0);
    setHasCompleted(false);
    setIsPlaying(false);
  };

  // Handle seek
  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    const newTime = (percentage / 100) * duration;
    
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
    setCurrentTime(newTime);
    setProgress(percentage);
    if (hasCompleted) {
      setHasCompleted(false);
    }
  };

  // Format duration for button display
  const formatDurationShort = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="w-full max-w-xl mx-auto" data-testid="audio-demo-player">
      {/* Main Button - Always Visible */}
      <AnimatePresence mode="wait">
        {!isExpanded ? (
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={togglePlay}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className="w-full group relative overflow-hidden"
            data-testid="audio-demo-btn"
          >
            <motion.div
              className="relative flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-[#FF6600] to-[#E55500] text-white font-bold shadow-lg"
              whileHover={{ 
                scale: 1.03,
                boxShadow: "0 0 40px rgba(255, 102, 0, 0.4)"
              }}
              whileTap={{ scale: 0.98 }}
              style={{ fontFamily: 'Inter, Montserrat, sans-serif' }}
            >
              {/* Animated background shimmer */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                animate={{
                  x: ["-100%", "100%"]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatDelay: 3
                }}
              />
              
              {/* Waveform Icon */}
              <div className="relative z-10">
                <WaveformIcon isPlaying={false} isHovered={isHovered} />
              </div>
              
              {/* Button Text */}
              <span className="relative z-10 text-sm md:text-base tracking-wide">
                Hear Amy Handle an Emergency Call
              </span>
              
              {/* Duration Badge */}
              <span className="relative z-10 bg-white/20 px-2 py-0.5 rounded-full text-xs font-medium">
                {formatDurationShort(duration)}
              </span>
            </motion.div>
          </motion.button>
        ) : (
          /* Expanded Audio Player */
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="relative rounded-2xl overflow-hidden"
            data-testid="audio-player-expanded"
          >
            {/* Glassmorphism Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a2e]/90 to-[#0d0d1a]/95 backdrop-blur-xl" />
            <div className="absolute inset-0 bg-white/[0.03]" />
            <div className="absolute inset-0 border border-white/10 rounded-2xl" />
            
            {/* Content */}
            <div className="relative p-6 space-y-5">
              {/* Header with Badge */}
              <div className="flex items-center justify-between">
                <motion.div 
                  className="flex items-center gap-2"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                  </span>
                  <span className="text-xs font-bold uppercase tracking-widest text-red-400">
                    Real-Time Recording
                  </span>
                </motion.div>
                
                {/* Close/Minimize */}
                <button 
                  onClick={() => { setIsExpanded(false); resetDemo(); }}
                  className="text-[#A0A0A0] hover:text-white transition-colors text-sm"
                >
                  Minimize
                </button>
              </div>
              
              {/* Caption */}
              <motion.div 
                className="space-y-1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <p className="text-white font-semibold text-base md:text-lg" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Amy (Voice AI) vs. Frustrated Homeowner
                </p>
                <p className="text-[#A0A0A0] text-sm">
                  Regarding a LG Dishwasher leak — Emergency dispatch call
                </p>
                <p className="text-[#A0A0A0]/60 text-xs italic mt-1">
                  * Private details (phone number & address) have been censored for privacy
                </p>
              </motion.div>
              
              {/* Waveform Visualization */}
              <motion.div 
                className="flex items-center justify-center gap-1 h-16 bg-white/5 rounded-xl"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                {Array.from({ length: 40 }).map((_, i) => (
                  <motion.div
                    key={i}
                    className="w-1 bg-gradient-to-t from-[#FF6600] to-[#FF4500] rounded-full"
                    animate={{
                      height: isPlaying 
                        ? Math.random() * 40 + 8
                        : 8 + Math.sin(i * 0.3) * 6
                    }}
                    transition={{
                      duration: isPlaying ? 0.15 : 0.5,
                      repeat: isPlaying ? Infinity : 0,
                      repeatType: "reverse"
                    }}
                    style={{ 
                      opacity: progress > (i / 40) * 100 ? 1 : 0.3
                    }}
                  />
                ))}
              </motion.div>
              
              {/* Progress Bar */}
              <ProgressBar 
                progress={progress} 
                duration={duration}
                currentTime={currentTime}
                onSeek={handleSeek}
              />
              
              {/* Controls */}
              <div className="flex items-center justify-center gap-4">
                <motion.button
                  onClick={togglePlay}
                  className="flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r from-[#FF6600] to-[#E55500] text-white shadow-lg"
                  whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(255, 102, 0, 0.5)" }}
                  whileTap={{ scale: 0.95 }}
                  data-testid="play-pause-btn"
                >
                  {isPlaying ? (
                    <Pause className="w-6 h-6" fill="white" />
                  ) : (
                    <Play className="w-6 h-6 ml-1" fill="white" />
                  )}
                </motion.button>
                
                {/* Volume indicator */}
                <div className="flex items-center gap-2 text-[#A0A0A0]">
                  <Volume2 className="w-4 h-4" />
                  <div className="w-16 h-1 bg-white/20 rounded-full">
                    <div className="w-3/4 h-full bg-white/60 rounded-full" />
                  </div>
                </div>
              </div>
              
              {/* Success Message - Post Play */}
              <AnimatePresence>
                {hasCompleted && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="flex items-center justify-center gap-3 p-4 bg-gradient-to-r from-emerald-500/20 to-green-500/10 border border-emerald-500/30 rounded-xl"
                    data-testid="success-message"
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", delay: 0.1 }}
                    >
                      <CheckCircle className="w-6 h-6 text-emerald-400" />
                    </motion.div>
                    <div className="text-center">
                      <p className="text-emerald-400 font-bold text-sm">
                        Success: Lead Captured
                      </p>
                      <p className="text-emerald-300/70 text-xs">
                        Cost: $1.10 • Booked for same-day service
                      </p>
                    </div>
                    <motion.button
                      onClick={resetDemo}
                      className="ml-2 text-xs text-[#A0A0A0] hover:text-white underline"
                      whileHover={{ scale: 1.05 }}
                    >
                      Replay
                    </motion.button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Real audio element */}
      <audio 
        ref={audioRef} 
        preload="metadata"
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
      >
        <source src="/audio/amy-demo.mp3" type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>
    </div>
  );
};

export default AudioDemoPlayer;
